import { Controller } from "@hotwired/stimulus"
import gsap from "gsap"
import ScrollTrigger from "gsap/ScrollTrigger"
import ScrollToPlugin from "gsap/ScrollToPlugin"

gsap.registerPlugin(ScrollTrigger, ScrollToPlugin)

export default class extends Controller {
  connect() {
    this.initSmoothScroll()
    this.initScrollProgress()
    this.setupScrollEffects()
    this.setupTurboNavigation()
  }

  setupTurboNavigation() {
    document.addEventListener('turbo:frame-load', (event) => {
      if (event.target.id === 'main_content') {
        // Instant scroll to top on navigation
        window.scrollTo(0, 0)

        // Refresh ScrollTriggers for new content
        ScrollTrigger.refresh()
      }
    })
  }

  initSmoothScroll() {
    // Enhanced smooth scroll for anchor links
    document.querySelectorAll('a[href^="#"]').forEach(anchor => {
      if (anchor.dataset.turbo === "false") return

      anchor.addEventListener('click', (e) => {
        const targetId = anchor.getAttribute('href')
        if (targetId === '#') return

        const target = document.querySelector(targetId)
        if (target) {
          e.preventDefault()

          // Visual feedback on link
          gsap.to(anchor, {
            scale: 0.95,
            duration: 0.1,
            yoyo: true,
            repeat: 1
          })

          // Smooth scroll with easing
          gsap.to(window, {
            duration: 1.5,
            scrollTo: {
              y: target,
              offsetY: 80,
              autoKill: true
            },
            ease: "power3.inOut"
          })
        }
      })
    })
  }

  initScrollProgress() {
    // Create enhanced scroll progress indicator
    const progressBar = document.createElement('div')
    progressBar.className = 'scroll-progress-bar'
    progressBar.style.cssText = `
position: fixed;
top: 0;
left: 0;
width: 0%;
height: 4px;
background: linear-gradient(90deg, #0ea5e9 0%, #10b981 50%, #f59e0b 100%);
z-index: 9999;
transform-origin: left;
box-shadow: 0 2px 10px rgba(14, 165, 233, 0.5);
transition: box-shadow 0.3s ease;
`
    document.body.appendChild(progressBar)

    // Animate progress bar
    gsap.to(progressBar, {
      width: "100%",
      ease: "none",
      scrollTrigger: {
        start: "top top",
        end: "bottom bottom",
        scrub: 0.3,
        onUpdate: (self) => {
          // Add glow effect based on progress
          const progress = self.progress
          const glowIntensity = 0.3 + (progress * 0.5)
          progressBar.style.boxShadow = `0 2px 10px rgba(14, 165, 233, ${glowIntensity})`
        }
      }
    })

    // Add pulse animation when reaching milestones
    ScrollTrigger.create({
      start: "25% top",
      onEnter: () => this.pulseProgress(progressBar),
      once: true
    })

    ScrollTrigger.create({
      start: "50% top",
      onEnter: () => this.pulseProgress(progressBar),
      once: true
    })

    ScrollTrigger.create({
      start: "75% top",
      onEnter: () => this.pulseProgress(progressBar),
      once: true
    })
  }

  pulseProgress(element) {
    gsap.to(element, {
      height: 6,
      duration: 0.3,
      yoyo: true,
      repeat: 1,
      ease: "power2.inOut"
    })
  }

  setupScrollEffects() {
    // Add scroll-based animations
    let lastScrollY = window.scrollY

    window.addEventListener('scroll', () => {
      const currentScrollY = window.scrollY
      const scrollingDown = currentScrollY > lastScrollY

      // Update navigation based on scroll direction
      const nav = document.querySelector('nav')
      if (nav) {
        if (scrollingDown && currentScrollY > 100) {
          gsap.to(nav, {
            y: -100,
            duration: 0.3,
            ease: "power2.inOut"
          })
        } else {
          gsap.to(nav, {
            y: 0,
            duration: 0.3,
            ease: "power2.inOut"
          })
        }
      }

      lastScrollY = currentScrollY
    }, { passive: true })
  }

  disconnect() {
    ScrollTrigger.getAll().forEach(trigger => trigger.kill())
    const progressBar = document.querySelector('.scroll-progress-bar')
    if (progressBar) progressBar.remove()
  }
}
